import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShoppingCart, Package, Minus, Plus, CheckSquare, XSquare } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const OrderStickersPage = () => {
  const [children, setChildren] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [allPurchases, setAllPurchases] = useState([]); // Store all purchases before filtering
  const [selectedItems, setSelectedItems] = useState({});
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterChildId, setFilterChildId] = useState('all'); // Filter by child
  const [calculatedTotal, setCalculatedTotal] = useState(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    loadPurchasedRewards();
  }, []);

  // Filter effect
  useEffect(() => {
    if (filterChildId && filterChildId !== 'all') {
      // Filter to show only purchases owned by selected child
      const filtered = allPurchases.filter(p => 
        p.owned_by_children && p.owned_by_children.includes(filterChildId)
      );
      setPurchases(filtered);
    } else {
      // Show all
      setPurchases(allPurchases);
    }
  }, [filterChildId, allPurchases]);

  const loadPurchasedRewards = async () => {
    try {
      setLoading(true);
      
      // Get all children and their purchases
      const childrenData = await api.getChildren();
      setChildren(childrenData);
      
      // Get all purchases for all children - keep track of which children own each
      const allPurchasesWithChildren = [];
      const purchasesByRewardId = {}; // Map reward_id to list of children who own it
      
      for (const child of childrenData) {
        const childPurchases = await api.request(`/api/rewards/purchases?child_id=${child.id}`);
        allPurchasesWithChildren.push(...childPurchases);
        
        // Track which children own each reward
        childPurchases.forEach(purchase => {
          if (!purchasesByRewardId[purchase.reward_id]) {
            purchasesByRewardId[purchase.reward_id] = [];
          }
          purchasesByRewardId[purchase.reward_id].push(child.id);
        });
      }
      
      // Deduplicate by reward_id (same sticker purchased by multiple children still counts as one unique item)
      // Use reward_id as the unique identifier throughout
      const uniquePurchases = Array.from(
        new Map(allPurchasesWithChildren.map(p => [p.reward_id, {
          ...p,
          id: p.reward_id,  // Use reward_id as the id to ensure uniqueness
          owned_by_children: purchasesByRewardId[p.reward_id] || [] // Track which children own it
        }])).values()
      );
      
      setAllPurchases(uniquePurchases); // Store all purchases
      setPurchases(uniquePurchases); // Initially show all
    } catch (error) {
      console.error('Failed to load purchases:', error);
      toast({
        title: 'Error',
        description: 'Failed to load purchased rewards',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (purchaseId) => {
    // If already selected, just toggle off
    if (selectedItems[purchaseId]) {
      setSelectedItems(prev => ({
        ...prev,
        [purchaseId]: false
      }));
      return;
    }
    
    // If not selected, select it
    setSelectedItems(prev => ({
      ...prev,
      [purchaseId]: true
    }));
    
    // If quantity already exists (from previous selection), increment it
    // Otherwise initialize to 1
    if (quantities[purchaseId]) {
      setQuantities(prev => ({
        ...prev,
        [purchaseId]: prev[purchaseId] + 1
      }));
    } else {
      setQuantities(prev => ({
        ...prev,
        [purchaseId]: 1
      }));
    }
  };

  const selectAll = () => {
    const newSelectedItems = {};
    const newQuantities = { ...quantities };
    
    purchases.forEach(purchase => {
      // If already selected, increment quantity
      if (selectedItems[purchase.id]) {
        newQuantities[purchase.id] = (newQuantities[purchase.id] || 1) + 1;
      } else {
        // Select it
        newSelectedItems[purchase.id] = true;
        // Initialize quantity if not exists
        if (!newQuantities[purchase.id]) {
          newQuantities[purchase.id] = 1;
        }
      }
    });
    
    setSelectedItems(prev => ({ ...prev, ...newSelectedItems }));
    setQuantities(newQuantities);
    
    toast({
      title: 'Selected All',
      description: `${purchases.length} stickers added to cart`,
    });
  };

  const deselectAll = () => {
    setSelectedItems({});
    setQuantities({});
    
    toast({
      title: 'Cart Cleared',
      description: 'All items removed from cart',
    });
  };

  const updateQuantity = (purchaseId, delta) => {
    setQuantities(prev => {
      const newQty = Math.max(1, Math.min(99, (prev[purchaseId] || 1) + delta));
      return {
        ...prev,
        [purchaseId]: newQty
      };
    });
  };

  const getSelectedPurchases = () => {
    // Get ALL selected items from allPurchases (not filtered purchases)
    // This ensures cart shows items from all children, not just the filtered view
    return allPurchases.filter(p => selectedItems[p.id]);
  };

  const calculateSubtotal = () => {
    return getSelectedPurchases().reduce((sum, p) => {
      const qty = quantities[p.id] || 1;
      return sum + (0.35 * qty);
    }, 0);
  };

  const getTotalItems = () => {
    return getSelectedPurchases().reduce((sum, p) => {
      return sum + (quantities[p.id] || 1);
    }, 0);
  };

  const proceedToCheckout = () => {
    const selected = getSelectedPurchases();
    if (selected.length === 0) {
      toast({
        title: 'No Items Selected',
        description: 'Please select at least one sticker to order',
        variant: 'destructive'
      });
      return;
    }

    // Pass selected items to checkout page
    navigate('/orders/stickers/checkout', {
      state: {
        items: selected.map(p => ({
          reward_id: p.reward_id,
          reward_name: p.reward_name,
          reward_type: p.reward_type,
          image_url: p.image_url,
          quantity: quantities[p.id] || 1,
          price_per_item: 0.35
        }))
      }
    });
  };

  const subtotal = calculateSubtotal();
  const freeShippingEligible = subtotal >= 20;
  const estimatedShipping = freeShippingEligible ? 0 : 3;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Order Physical Stickers</h1>
          <p className="text-slate-600">Select stickers your children have earned to order physical copies</p>
        </div>

        {/* Info Banner */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Package className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Pricing & Shipping</p>
                <ul className="list-disc list-inside space-y-1 text-blue-800">
                  <li>$0.35 per sticker</li>
                  <li>FREE shipping on orders $20 or more</li>
                  <li>$3 flat shipping for contiguous US</li>
                  <li>$5 shipping to Alaska and Hawaii</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stickers Selection */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Available Stickers ({purchases.length})</CardTitle>
                <CardDescription>
                  Select stickers to order. Your children have earned these rewards.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Filter & Select All Controls */}
                <div className="mb-4 flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Label className="text-xs mb-1 block">Filter by Child</Label>
                    <Select value={filterChildId} onValueChange={setFilterChildId}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="All Children" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Children</SelectItem>
                        {children.map(child => (
                          <SelectItem key={child.id} value={child.id}>
                            {child.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={selectAll}
                      variant="outline"
                      size="sm"
                      disabled={purchases.length === 0}
                      className="whitespace-nowrap"
                    >
                      <CheckSquare className="w-4 h-4 mr-2" />
                      Select All
                    </Button>
                    <Button
                      onClick={deselectAll}
                      variant="outline"
                      size="sm"
                      disabled={Object.keys(selectedItems).length === 0}
                      className="whitespace-nowrap"
                    >
                      <XSquare className="w-4 h-4 mr-2" />
                      Clear
                    </Button>
                  </div>
                </div>

                {purchases.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No stickers {filterChildId ? 'for this child' : 'purchased yet'}</p>
                    <p className="text-sm mt-2">{filterChildId ? 'Try selecting a different child' : 'Children need to purchase stickers from the reward store first'}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {purchases.map((purchase) => (
                      <Card
                        key={purchase.id}
                        className={`overflow-hidden cursor-pointer transition-all ${
                          selectedItems[purchase.id]
                            ? 'ring-2 ring-blue-500 shadow-lg'
                            : 'hover:shadow-md'
                        }`}
                        onClick={() => toggleSelection(purchase.id)}
                      >
                        {/* Image */}
                        <div className="aspect-square bg-gradient-to-br from-purple-50 to-pink-50 p-4 flex items-center justify-center relative">
                          {purchase.image_url ? (
                            <img
                              src={purchase.image_url}
                              alt={purchase.reward_name}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                // Fallback to icon if image fails to load
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                          ) : null}
                          <div 
                            className="text-5xl"
                            style={{ display: purchase.image_url ? 'none' : 'block' }}
                          >
                            {purchase.reward_icon || '🎁'}
                          </div>
                          
                          {/* Checkbox */}
                          <div 
                            className="absolute top-2 left-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={selectedItems[purchase.id] || false}
                              onCheckedChange={() => toggleSelection(purchase.id)}
                              className="bg-white"
                            />
                          </div>
                        </div>

                        {/* Info */}
                        <div className="p-3">
                          <h3 className="font-semibold text-sm line-clamp-1">{purchase.reward_name}</h3>
                          <Badge variant="secondary" className="text-xs mt-1">
                            {purchase.reward_type}
                          </Badge>
                          
                          {/* Quantity Selector */}
                          {selectedItems[purchase.id] && (
                            <div className="mt-2 flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 w-7 p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateQuantity(purchase.id, -1);
                                }}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="text-sm font-semibold w-8 text-center">
                                {quantities[purchase.id] || 1}
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 w-7 p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateQuantity(purchase.id, 1);
                                }}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Items:</span>
                    <span className="font-semibold">{getTotalItems()}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Subtotal:</span>
                    <span className="font-semibold">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Est. Shipping:</span>
                    <span className={`font-semibold ${freeShippingEligible ? 'text-green-600' : ''}`}>
                      {freeShippingEligible ? 'FREE' : `$${estimatedShipping.toFixed(2)}`}
                    </span>
                  </div>
                  {freeShippingEligible && (
                    <Badge className="w-full justify-center bg-green-500">
                      Free Shipping Unlocked! 🎉
                    </Badge>
                  )}
                  {!freeShippingEligible && subtotal > 0 && (
                    <p className="text-xs text-slate-500 text-center">
                      Add ${(20 - subtotal).toFixed(2)} more for free shipping
                    </p>
                  )}
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between mb-4">
                    <span className="font-bold">Estimated Total:</span>
                    <span className="font-bold text-lg">
                      ${(subtotal + estimatedShipping).toFixed(2)}
                    </span>
                  </div>

                  <Button
                    onClick={proceedToCheckout}
                    disabled={getTotalItems() === 0}
                    className="w-full"
                  >
                    Proceed to Checkout
                  </Button>
                </div>

                <div className="text-xs text-slate-500 space-y-1">
                  <p>• Physical stickers will be mailed to your address</p>
                  <p>• Processing time: 2-3 business days</p>
                  <p>• Shipping time: 3-5 business days</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderStickersPage;
